import styles from "./styles.module.scss";
import {
  FC,
  useEffect,
  useState,
  useContext,
  useRef,
  FunctionComponent,
  SVGProps,
  forwardRef,
} from "react";
import * as openpgp from "openpgp";
import { IFolderLink, Props } from "./typings";
import { getBlob } from "utils/getBlob";
import KeysContext from "contexts/KeysContext";
import { decryptFile } from "utils/decryptFile";
import { useAuth } from "react-oidc-context";
import { getUserId } from "utils/getUserId";
import { ItemKind } from "dto/IKindItems";
import { readFileFromBase64 } from "utils/readFileFromBase64";
import Spinner from "components/Spinner";
import usePDF from "hooks/usePDF";
import ErrorScreen from "components/ErrorScreen";
import CloseIcon from "assets/icons/close.svg?react";
import BackIcon from "assets/icons/arrowBack.svg?react";
import Typography from "@mui/material/Typography";
import FolderIcon from "assets/icons/folder_24px.svg?react";
import { getFile as GetFile } from "utils/getFile";
import { treeItemClasses, useTreeItemState } from "@mui/x-tree-view/TreeItem";
import classNames from "classnames";
import HollowButton from "components/HollowButton";
import Tooltip from "@mui/material/Tooltip";
import { alpha, Box, Collapse, styled } from "@mui/material";
import { useDownloadFileStore } from "store/downloadFile.store";
import LayoutContext, { ILayoutSizes } from "contexts/LayoutContext";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useDrag, useDrop } from "react-dnd";
import { IDossierItem } from "dto/Dossier/IDossierItem";
import dossierService, { IDossierAddFile } from "api/dossierService";
import { DossierItemType } from "dto/Dossier/DossierItemType";
import { IDossierFolder } from "dto/Dossier/IDossierFolder";
import { logger } from "utils/logger";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { useTreeViewApiRef } from "@mui/x-tree-view/hooks/useTreeViewApiRef";
import PDFIcon from "assets/icons/pdf.svg?react";
import XLSIcon from "assets/icons/xls.svg?react";
import OtherIcon from "assets/icons/other.svg?react";
import PngIcon from "assets/icons/png.svg?react";
import JpgIcon from "assets/icons/jpg.svg?react";
import DocIcon from "assets/icons/doc.svg?react";
import TXTIcon from "assets/icons/txt.svg?react";
import PptIcon from "assets/icons/ppt.svg?react";
import RarIcon from "assets/icons/rar.svg?react";
import ZipIcon from "assets/icons/zip.svg?react";
import PsdIcon from "assets/icons/psd.svg?react";
import SvgIcon from "assets/icons/svg.svg?react";
import GifIcon from "assets/icons/gif.svg?react";
import MovIcon from "assets/icons/mov.svg?react";
import { animated, useSpring } from "@react-spring/web";
import { TransitionProps } from "@mui/material/transitions";
import {
  TreeItem2Checkbox,
  TreeItem2IconContainer,
  TreeItem2Label,
  TreeItem2Root,
  TreeItem2Content,
} from "@mui/x-tree-view/TreeItem2";
import { UseTreeItem2Parameters } from "@mui/x-tree-view/useTreeItem2";
import { useTreeItem2 } from "@mui/x-tree-view/useTreeItem2/useTreeItem2";
import clsx from "clsx";
import { TreeItem2Icon } from "@mui/x-tree-view/TreeItem2Icon";
import Button from "components/Button";
import { TreeItem2Provider } from "@mui/x-tree-view/TreeItem2Provider";
import SearchCasesNew from "components/SearchCasesNew";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import { IChatMessageAttachment } from "dto/Chat/IChatMessageAttachment";
import { axiosInstance } from "api/axios";
import { handleCreate } from "utils/uploadFile";
import { useTranslation } from "react-i18next";

const ChatMessageAttachToDossierModal: FC<Props> = ({
  caseId, // eslint-disable-line @typescript-eslint/no-unused-vars
  message,
  encryptionKeyId,
  toggle,
  refetch, // eslint-disable-line @typescript-eslint/no-unused-vars
}) => {
  const { t } = useTranslation();
  useDownloadFileStore();

  const [targetCase, setTargetCase] = useState<ICaseSimple>(null);

  const { data: foldersData } = dossierService.useGetFolders(
    targetCase?.Id ?? "none",
    targetCase?.Id != null
  );
  const { mutate: createFolder } = dossierService.useCreateFolder();
  const [folders, setFolders] = useState<IDossierItem[]>([]);
  const [foldersTmp, setFoldersTmp] = useState<IDossierItem[]>([]); // eslint-disable-line @typescript-eslint/no-unused-vars

  const [newFolderName, setNewFolderName] = useState<string>("");

  const [newFolderParent, setNewFolderParent] = useState<IDossierItem>(null);
  const [newFolderItem, setNewFolderItem] = useState<IDossierItem>(null);
  const refNewFolderInput = useRef(null);
  const [selectedItems, setSelectedItems] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [expandedZipItems, setExpandedZipItems] = useState<string[]>([]);

  const [selectedAttachments, setSelectedAttachments] = useState< // eslint-disable-line @typescript-eslint/no-unused-vars
    IChatMessageAttachment[]
  >([]);

  const { isLoading: isLoading2 } = usePDF();

  const ref = useRef(null);
  const [sizes, setSizes] = useState<ILayoutSizes>({
    width: 50,
    height: 50,
    isDraging: false,
  });

  const auth = useAuth();
  const userId = getUserId(auth.user!);
  const apiRefZip = useTreeViewApiRef();
  const apiRef = useTreeViewApiRef();

  const rootItem: IDossierItem = {
    Id: targetCase?.Id ?? "none",
    Label: "Dossier",
    CaseId: targetCase?.Id ?? "none",

    Kind: ItemKind.DossierFolder,
    Type: DossierItemType.Folder,
    DeskInfo_IsOn: false,
    TagCount: 0,
    IsShared: false,
    CommentsCount: 0,
    CreationDate: new Date(),
    LastModified: new Date(),
    DossierId: targetCase?.Id ?? "none",
    Keys: [],
    ObjectKeys: [],
    children: [],
  };

  const arrayToTree = (items: IDossierFolder[]) => {
    const rootItems: IDossierItem[] = [];
    const lookup: { [Id: string]: IDossierItem } = {};

    // Initialize lookup table and create a tree node for each item
    for (const item of items) {
      lookup[item.Id] = {
        ...item,
        Label: item.Name,
        children: [],
        Kind: ItemKind.DossierFolder,
        Type: DossierItemType.Folder,
        DeskInfo_IsOn: false,
        TagCount: 0,
        IsShared: false,
        CommentsCount: 0,
        CreationDate: new Date(),
        LastModified: new Date(),
        DossierId: item.Id,
        Keys: [],
        ObjectKeys: [],
      };
    }

    // Build the tree
    for (const item of items) {
      if (!item.ParentId) {
        rootItems.push(lookup[item.Id]);
      } else {
        const parent = lookup[item.ParentId!];
        if (parent) {
          parent.children!.push(lookup[item.Id]);
        }
      }
    }

    return rootItems;
  };

  const findElement = (
    id: string,
    items: IDossierItem[]
  ): IDossierItem | null => {
    const el = items.find((x) => x.Id === id);

    if (el) {
      return el;
    }

    for (let index = 0; index < items.length; index++) {
      const element = items[index];

      if (element.children) {
        const result = findElement(id, element.children);
        if (result) return result;
      }
    }

    return null;
  };

  const findElementParent = (
    id: string,
    items: IDossierItem[],
    parent: IDossierItem
  ): IDossierItem | null => {
    const el = items.find((x) => x.Id === id);

    if (el) {
      return parent;
    }

    for (let index = 0; index < items.length; index++) {
      const element = items[index];

      if (element.children) {
        const result = findElementParent(id, element.children, element);
        if (result) return result;
      }
    }

    return null;
  };

  const getAllItemsWithChildrenItemIds = () => {
    const itemIds: string[] = [];
    const registerItemId = (item: IDossierItem) => {
      if (item.children?.length) {
        itemIds.push(item.Id);
        item.children.forEach(registerItemId);
      }
    };

    folders.forEach(registerItemId);

    return itemIds;
  };

  const [showFileTooBigMessage, setShowFilleToBigMessage] = // eslint-disable-line @typescript-eslint/no-unused-vars
    useState<boolean>(false);

  const [fileList, setFileList] = useState<IChatMessageAttachment[]>([]);
  const [usedAttachments, setUsedAttachments] = useState<IFolderLink[]>([]);

  const [showPreview, setShowPreview] = useState<boolean>(false);

  const handleCreateFolder = () => {
    setNewFolderName("");
    const folderItem = findElement(selectedItems, folders) ?? rootItem;
    logger.log("Creating folder, parent:", folderItem);

    if (!folderItem || folderItem.Type != DossierItemType.Folder) return;

    const newFolder: IDossierItem = {
      Id: "newItem",
      Label: t("dossier.newFolder"),
      CaseId: targetCase?.Id ?? "none",

      Kind: ItemKind.DossierFolder,
      Type: DossierItemType.Folder,
      DeskInfo_IsOn: false,
      TagCount: 0,
      IsShared: false,
      CommentsCount: 0,
      CreationDate: new Date(),
      LastModified: new Date(),
      DossierId: targetCase?.Id ?? "none",
      Keys: [],
      ObjectKeys: [],
      isNew: true,
      children: [],
    };

    folderItem.children!.push(newFolder);

    setNewFolderItem(newFolder);
    setNewFolderParent(folderItem);

    setExpandedItems(getAllItemsWithChildrenItemIds());
  };

  useEffect(() => {
    setFileList(message.Attachments);
  }, []);


  const commitNewFolder = (item: IDossierItem, label: string) => {
    logger.log("Commiting new folder", newFolderItem);
    if (newFolderItem != null) {
      createFolder(
        {
          CaseId: newFolderParent.CaseId,
          ParentId:
            newFolderParent.Id != rootItem.Id ? newFolderParent.Id : null,
          Name: label,
        },
        {
          onSuccess: (d) => {
            const newFolders = [...folders];
            newFolderItem.Id = d.data.Id;
            newFolderItem.DossierId = d.data.Id;
            newFolderItem.CaseId = d.data.CaseId;
            newFolderItem.Label = label;
            newFolderItem.ParentId = d.data.ParentId;

            newFolderItem.isNew = false;
            setNewFolderItem(null);
            setNewFolderParent(null);

            setFolders(newFolders);
          },
        }
      );
    }
  };

  const cancelNewFolder = () => {
    if (newFolderItem != null) {
      newFolderParent.children.pop();
      setNewFolderItem(null);
      setNewFolderParent(null);
    }
  };

  const handleSelectedItemsChange = (
    event: React.SyntheticEvent,
    id: string | null
  ) => {
    setSelectedItems(id);
  };

  const handleExpandedItemsChange = (
    event: React.SyntheticEvent,
    itemIds: string[]
  ) => {
    setExpandedItems(itemIds);
  };

  const handleExpandedZipItemsChange = (
    event: React.SyntheticEvent,
    itemIds: string[]
  ) => {
    setExpandedZipItems(itemIds);
  };

  const { mutate: addFileToDossier } = dossierService.useAddFile();
  const { setProgress } = useDownloadFileStore();
  const { keys } = useContext(KeysContext);

  const handleAdd = async () => {
    const respo = await axiosInstance.get(`/case/keys?Id=${targetCase.Id}`);
    const casePublicKeyData = await readFileFromBase64(
      respo.data[0].PublicKey,
      "application/other"
    );

    const casePublicKey = await openpgp.key.read(
      new Uint8Array(casePublicKeyData)
    );

    for (let i = 0; i < usedAttachments.length; i++) {
      const element = usedAttachments[i];
      console.log("Attaching", element);
      const att = message.Attachments.find((x) => x.Id === element.AttachmentId);

      const fileBlob = await GetFile(att?.Uri ?? "");
      const blob = await getBlob(fileBlob.data.Url);

      const key = keys.find((key: any) => key?.Id === encryptionKeyId);

      if (!key) return;

      let parentKey = null;

      if (key.ParentKeyId) {
        parentKey = keys.find(
          //@ts-ignore
          (pkey: any) => pkey?.Id === key?.ParentKeyId
        );
      }

      const decryptedFile = await decryptFile(
        userId ?? "",
        new Blob([blob.data]),
        key,
        parentKey
      );

      // setId(element.id);
      // setProgress(0);

      const r = await handleCreate(
        [decryptedFile],
        targetCase.Id,
        casePublicKey,
        true,
        true,
        att.FileName,
        att.ContentType,
        (fileIndex: number, progress: number) => setProgress(progress)
      );

      const payload: IDossierAddFile = {
        CaseId: targetCase.Id,
        ParentId: element.FolderId === rootItem.Id ? null : element.FolderId,
        Label: att.FileName,
        Files: [
          //@ts-ignore
          {
            KeyId: r[0].keyId,
            Uri: r[0].uri,
          },
        ],
      };
      addFileToDossier(payload, {
        onSuccess: () => {
          toggle(false);
        },
      });
    }
  };

  const handleItemFocus = (itemId: string) => { // eslint-disable-line @typescript-eslint/no-unused-vars
    // event.preventDefault();
    // if (!event.currentTarget) {
    //   return;
    // }
    // const rowId = event.target.getAttribute("id");
    // var item = apiRef.current.getItem(
    //   // The id of the item to retrieve
    //   rowId
    // );
    // console.log(rowId, item);
    // const record = fileList.find((row) => row.Id === itemId);
    // if (!record) {
    //   return;
    // }
    // if (record.isFolder) {
    // } else {
    //   const zip = new JSZip();
    //   const zipContent = zip
    //     .loadAsync(blob, {
    //       // decodeFileName: (bytes) => new TextDecoder("ISO-8859-2").decode(bytes),
    //     })
    //     .then((z: JSZip) => {
    //       z.files[record.fullName]
    //         .async("blob")
    //         .then((b) => addBlob(b, record.name));
    //     });
    //   setShowPreview(true);
    // }
    // if (record?.Type === DossierItemType.Folder) {
    //   onFolderDoubleClick(record);
    // } else {
    //   handleFileDoubleClick(record);
    // }
  };

  const [, dropRef] = useDrop({
    accept: "attachment",
    drop: (d) => onListDrop(d.Id),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: true,
    }),
  });

  const onListDrop = (attachmentId: string) => { // eslint-disable-line @typescript-eslint/no-unused-vars
    // var currentParent = findElementParent(attachmentId, folders, null);
    // var currentElement = unusedAttachments.find((x) => x.Id === attachmentId);
    // if (currentElement) return;
    // if (currentParent != null) {
    //   var toRemove = currentParent.children.find((x) => x.Id === attachmentId);
    //   if (toRemove) {
    //     const index = currentParent.children.indexOf(toRemove, 0);
    //     if (index > -1) {
    //       currentParent.children.splice(index, 1);
    //     }
    //   }
    //   var toReturn = pack.Attachments.find((x) => x.Id === attachmentId);
    //   setUnusedAttachments([...unusedAttachments, toReturn]);
    // }
  };

  useEffect(() => {
    if (foldersData) {
      rootItem.children = arrayToTree(
        //@ts-ignore
        foldersData as IDossierFolder[]
      );
      setFolders([rootItem]);
      setFoldersTmp([rootItem]);
    }
  }, [foldersData]);

  useEffect(() => {
    setExpandedItems(getAllItemsWithChildrenItemIds());
  }, [folders]);

  const linkAttachmentToFolder = (
    attachment: IChatMessageAttachment,
    folderItem: IDossierItem
  ) => {
    const link = {
      AttachmentId: attachment.Id,
      FolderId: folderItem.Id != rootItem.Id ? folderItem.Id : null,
    };

    setFileList((old) => old.filter((x) => x.Id != attachment.Id));
    setUsedAttachments((old) => [...old, link]);

    if (folderItem) {
      if (folderItem.children) {
        folderItem.children!.push({
          Id: attachment.Id.toString(),
          Label: attachment.FileName,
          FileName: attachment.FileName,
          CaseId: targetCase?.Id ?? "none",

          Kind: ItemKind.ContactMessage,
          Type: DossierItemType.Attachment,
          DeskInfo_IsOn: false,
          TagCount: 0,
          IsShared: false,
          CommentsCount: 0,
          CreationDate: new Date(),
          LastModified: new Date(),
          DossierId: targetCase?.Id ?? "none",
          Keys: [],
          ObjectKeys: [],
        });
        setExpandedItems(getAllItemsWithChildrenItemIds());
      }
    }
  };

  const StyledTreeItemRoot = styled(TreeItem2Root)(({ theme }) => ({
    color:
      theme.palette.mode === "light"
        ? theme.palette.grey[800]
        : theme.palette.grey[400],
    position: "relative",
    [`& .${treeItemClasses.groupTransition}`]: {
      marginLeft: theme.spacing(3.5),
    },
  })) as unknown as typeof TreeItem2Root;

  const CustomTreeItemContent = styled(TreeItem2Content)(({ theme }) => ({
    flexDirection: "row-reverse",
    borderRadius: theme.spacing(0.7),
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    paddingRight: theme.spacing(1),
    background: "transparent",
    fontWeight: 500,
    [`&.Mui-expanded `]: {
      "&:not(.Mui-focused, .Mui-selected, .Mui-selected.Mui-focused) .labelIcon":
        {
          color:
            theme.palette.mode === "light"
              ? theme.palette.primary.main
              : theme.palette.primary.dark,
        },
      "&::before": {
        content: '""',
        display: "block",
        position: "absolute",
        left: "16px",
        top: "44px",
        height: "calc(100% - 48px)",
        width: "1.5px",
        backgroundColor:
          theme.palette.mode === "light"
            ? theme.palette.grey[300]
            : theme.palette.grey[700],
      },
    },
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color:
        theme.palette.mode === "light" ? theme.palette.primary.main : "white",
    },
    [`&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused`]: {
      backgroundColor:
        theme.palette.mode === "light" ? "#e6defe" : theme.palette.primary.dark,
      // color: theme.palette.primary.contrastText,
    },
  }));

  const AnimatedCollapse = animated(Collapse);

  function TransitionComponent(props: TransitionProps) {
    const style = useSpring({
      to: {
        opacity: props.in ? 1 : 0,
        transform: `translate3d(0,${props.in ? 0 : 20}px,0)`,
      },
    });

    return <AnimatedCollapse style={style} {...props} />;
  }

  const StyledTreeItemLabelText = styled(Typography)({
    color: "inherit",
    fontWeight: 500,
  }) as unknown as typeof Typography;

  interface CustomLabelProps {
    children: React.ReactNode;
    icon?: React.ElementType;
    expandable?: boolean;
  }

  function CustomLabel({
    icon: Icon,
    expandable, // eslint-disable-line @typescript-eslint/no-unused-vars
    children,
    ...other
  }: CustomLabelProps) {
    return (
      <TreeItem2Label
        {...other}
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        {Icon && (
          <Box
            component={Icon}
            className="labelIcon"
            color="inherit"
            sx={{ mr: 1, fontSize: "1.2rem" }}
          />
        )}

        <StyledTreeItemLabelText variant="body2">
          {children}
        </StyledTreeItemLabelText>
        {/* {expandable && <DotIcon />} */}
      </TreeItem2Label>
    );
  }

  const isExpandable = (reactChildren: React.ReactNode) => {
    if (Array.isArray(reactChildren)) {
      return reactChildren.length > 0 && reactChildren.some(isExpandable);
    }
    return Boolean(reactChildren);
  };

  const getIconFromFileType = (
    item: IDossierItem | IChatMessageAttachment
  ): FunctionComponent<
    SVGProps<SVGSVGElement> & {
      title?: string;
    }
  > => {
    if (!item) return OtherIcon;

    if ("Type" in item && item.Type === DossierItemType.Folder)
      return FolderIcon;

    if ("isFolder" in item && item.isFolder) return FolderIcon;

    const fname =
      "FileName" in item
        ? item.FileName
        : (("name" in item ? item.name : "") as string);
    const name = fname.split(".");
    const extenstion = name[name.length - 1]?.toLocaleLowerCase();

    switch (extenstion) {
      case "jpg":
        return JpgIcon;
      case "gif":
        return GifIcon;
      case "xlsx":
      case "xls":
        return XLSIcon;
      case "pdf":
        return PDFIcon;
      case "png":
        return PngIcon;
      case "doc":
      case "docx":
        return DocIcon;
      case "ppt":
      case "pptx":
        return PptIcon;
      case "txt":
        return TXTIcon;
      case "zip":
      case "7z":
        return ZipIcon;
      case "rar":
        return RarIcon;
      case "mov":
        return MovIcon;
      case "psd":
        return PsdIcon;
      case "svg":
        return SvgIcon;
      default:
        return OtherIcon;
    }
  };

  interface CustomTreeItemProps
    extends Omit<UseTreeItem2Parameters, "rootRef">,
      Omit<React.HTMLAttributes<HTMLLIElement>, "onFocus"> {}

  const dropGlobal = (folderId: string, attachmentId: number) => {
    const folderItem = findElement(folderId, folders);

    if (!folderItem) {
      return;
    }

    if (selectedAttachments.length > 1) {
      selectedAttachments.forEach((a) => {
        const attachment = fileList.find((x) => x.Id === a.Id);
        if (!attachment) {
          return;
        }

        linkAttachmentToFolder(attachment, folderItem);
      });
    } else {
      console.log("Drop global", folderId, attachmentId);
      const attachment = fileList.find((x) => x.Id === attachmentId);

      if (!attachment) {
        // sprawdzamy, czy nie jest to przenoszenie na drzewie..
        const currentParent = findElementParent(
          attachmentId.toString(),
          folders,
          null
        );

        if (currentParent) {
          const toRemove = currentParent.children.find(
            (x) => x.Id === attachmentId.toString()
          );

          if (toRemove) {
            const index = currentParent.children.indexOf(toRemove, 0);
            if (index > -1) {
              currentParent.children.splice(index, 1);
            }

            folderItem.children.push(toRemove);
            setExpandedItems(getAllItemsWithChildrenItemIds());
          }
        }
        return;
      }

      linkAttachmentToFolder(attachment, folderItem);
    }
  };

  const CustomTreeItem = forwardRef(function CustomTreeItem(
    props: CustomTreeItemProps,
    ref: React.Ref<HTMLLIElement>
  ) {
    const {
      children,
      className, // eslint-disable-line @typescript-eslint/no-unused-vars
      itemId,
      id,
      label,
      onClick, // eslint-disable-line @typescript-eslint/no-unused-vars
      onMouseDown, // eslint-disable-line @typescript-eslint/no-unused-vars
      onBlur, // eslint-disable-line @typescript-eslint/no-unused-vars
      onKeyDown, // eslint-disable-line @typescript-eslint/no-unused-vars
      ...other
    } = props;

    const { disabled } =
      useTreeItemState(itemId);

    const {
      getRootProps,
      getContentProps,
      getIconContainerProps,
      getCheckboxProps,
      getLabelProps,
      getGroupTransitionProps,
      status,
      publicAPI,
    } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });

    const contentRef = useRef(null);
    // @ts-ignore
    const item = publicAPI.getItem(itemId);
    const expandable = isExpandable(children);

    const onDrop = (d) => {
      dropGlobal(itemId, d.id);
    };

    const [{ isOver }, drop] = useDrop({
      accept:
        item?.Type !== DossierItemType.Attachment ? ["attachment"] : "none",
      drop: onDrop,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    });

    const [, dragRef] = useDrag(
      () => ({
        type: "attachment",
        canDrag: () => "FileName" in item,
        item: { id: item?.Id },
        collect: (monitor) => ({
          opacity: monitor.isDragging() ? 0.5 : 1,
        }),
      }),
      []
    );

    const handleDoubleClick = () => {
      if ("name" in item) {
        handleItemFocus(item.id);
      }
    };

    // const handleAttach = () => {
    //   setShowAttachments(true);
    //   if (attListRef.current) {
    //     var parent = parentRef.current.getBoundingClientRect();
    //     var row = iconRef.current.getBoundingClientRect();
    //     setModalMode(row.y - parent.y + 28);
    //   }
    //   setSelectedItems(props.id);
    // };

    let icon = FolderIcon;
    icon = getIconFromFileType(item);

    return (
      // @ts-ignore
      <TreeItem2Provider itemId={itemId} key={"k" + itemId}>
        <StyledTreeItemRoot
          key={"root" + itemId}
          {...getRootProps({
            other,
          })}
        >
          <div ref={drop} onDoubleClick={handleDoubleClick}>
            <div ref={dragRef}>
              <CustomTreeItemContent
                key={"content" + itemId}
                ref={contentRef}
                {...getContentProps({
                  className: clsx("content", {
                    "Mui-expanded": status.expanded,
                    "Mui-selected": status.selected,
                    "Mui-focused": status.focused || isOver,
                    "Mui-disabled": status.disabled,
                  }),
                })}
              >
                <TreeItem2IconContainer {...getIconContainerProps()}>
                  <TreeItem2Icon status={status} />
                </TreeItem2IconContainer>
                <TreeItem2Checkbox {...getCheckboxProps()} />

                {!item?.isNew && (
                  <CustomLabel
                    {...getLabelProps({
                      icon,
                      expandable: expandable && status.expanded,
                    })}
                  />
                )}

                {item?.isNew && (
                  <div className={styles.inlineEditor}>
                    <input
                      ref={refNewFolderInput}
                      autoFocus={true}
                      className={styles.newFolderInput}
                      key="input_newFolderrr"
                      defaultValue={newFolderName}
                      onBlur={(e) => setNewFolderName(e.target.value)}
                      placeholder={t("dossier.newFolder")}
                      id="input_newFolder"
                      onKeyDownCapture={(e) => {
                        if (e.key === "Enter") {
                          const i = e.target as HTMLInputElement;
                          commitNewFolder(item, i.value);
                        } else if (e.key === "Escape") {
                          cancelNewFolder();
                        }
                        e.stopPropagation();
                      }}
                      onClickCapture={(e) => e.stopPropagation()}
                    ></input>

                    <HollowButton
                      className={styles.smallButton}
                      text={t("common.cancel")}
                      onClick={() => {
                        cancelNewFolder();
                      }}
                    ></HollowButton>
                    <Button
                      text={t("common.save")}
                      onClick={() =>
                        commitNewFolder(
                          item,
                          (refNewFolderInput.current as HTMLInputElement).value
                        )
                      }
                      className={styles.smallButton}
                    ></Button>
                  </div>
                )}
              </CustomTreeItemContent>
              {/* {provided.placeholder} */}
            </div>
          </div>
          {children && <TransitionComponent {...getGroupTransitionProps()} />}
        </StyledTreeItemRoot>
      </TreeItem2Provider>
    );
  });

  return (
    <div className={styles.modal}>
      <div className={styles.bg}></div>
      <div
        className={classNames(
          styles.wrapper,
          showPreview ? styles.withPreview : null
        )}
      >
        <div className={styles.content}>
          <div className={styles.main}>
            <div className={styles.hdr}>
              <div className={styles.title}>{t("chat.addToDossier")}</div>
              {!showPreview && (
                <CloseIcon
                  onClick={() => {
                    toggle(false);
                  }}
                  className={styles.closeIcon}
                />
              )}
            </div>

            {
              <div className={styles.grow}>
                <LayoutContext.Provider
                  value={{
                    sizes,
                    setSizes,
                  }}
                >
                  <PanelGroup ref={ref} direction="vertical">
                    <Panel defaultSize={50}>
                      <div className={classNames(styles.list)} ref={dropRef}>
                        <RichTreeView
                          apiRef={apiRefZip}
                          items={fileList}
                          getItemId={(x: IChatMessageAttachment) =>
                            x.Id.toString()
                          }
                          getItemLabel={(x: IChatMessageAttachment) =>
                            x.FileName
                          }
                          slots={{ item: CustomTreeItem }}
                          expandedItems={expandedZipItems}
                          onExpandedItemsChange={handleExpandedZipItemsChange}
                          expansionTrigger="iconContainer"
                          multiSelect={false}
                        />
                      </div>
                    </Panel>
                    <PanelResizeHandle
                      onDragging={(isDraging) => {
                        setSizes({ ...sizes, isDraging });
                      }}
                      className={`${styles.splitter}`}
                      id={"attachAttachments"}
                    ></PanelResizeHandle>

                    <Panel defaultSize={50}>
                      <div className={classNames(styles.listWrapper)}>
                        <div className={styles.caseSearch}>
                          <SearchCasesNew
                            settedCase={targetCase}
                            onChoose={(c) => {
                              setTargetCase(c);
                            }}
                            setAddNewEntity={() => {}}
                            className={styles.caseInput}
                            allowCreateNew={false}
                          ></SearchCasesNew>
                        </div>
                        <div className={classNames(styles.list)}>
                          <RichTreeView
                            apiRef={apiRef}
                            items={folders}
                            getItemId={(x: IDossierItem) => x.Id}
                            getItemLabel={(x: IDossierItem) => x.Label ?? ""}
                            slots={{ item: CustomTreeItem }}
                            defaultExpandedItems={[
                              targetCase?.Id ?? "none",
                              rootItem.Id,
                            ]}
                            expandedItems={expandedItems}
                            onExpandedItemsChange={handleExpandedItemsChange}
                            expansionTrigger="iconContainer"
                            multiSelect={false}
                            selectedItems={selectedItems}
                            onSelectedItemsChange={handleSelectedItemsChange}
                            onItemFocus={() => {
                              const input =
                                document.getElementById(`input_newFolder`);
                              if (input) input.focus();
                            }}
                          />
                        </div>
                        <div className={styles.buttons}>
                          <HollowButton
                            text={t("zip.newFolderPlus")}
                            onClick={(e) => {
                              e?.preventDefault();
                              e?.stopPropagation();
                              handleCreateFolder(e);
                            }}
                            disabled={!selectedItems}
                            className={styles.hollowButton}
                          ></HollowButton>
                        </div>
                      </div>
                    </Panel>
                  </PanelGroup>
                </LayoutContext.Provider>
              </div>
            }

            <div className={styles.footer}>
              <Button text={t("chat.addToDossier")} onClick={handleAdd}></Button>
            </div>

            {showFileTooBigMessage && (
              <ErrorScreen text={t("errors.archiveTooLarge")}></ErrorScreen>
            )}
          </div>
          <div
            className={classNames(
              styles.preview,
              showPreview ? styles.show : null
            )}
          >
            <div className={styles.toolbarFake}>
              <HollowButton
                text=""
                element={
                  <Tooltip title={t("common.closePreview")}>
                    <div>
                      <BackIcon></BackIcon>
                    </div>
                  </Tooltip>
                }
                onClick={() => setShowPreview(false)}
              ></HollowButton>
            </div>
            <div className={styles.content}>
              {isLoading2 && (
                <div className={styles.loading}>
                  <Spinner></Spinner>
                </div>
              )}
              {/* {!isLoading2 && blob && !showInstallPlugin && (
                <PDFViewer
                  className={styles.viewer}
                  file={previewBlob}
                  sourceFile={previewBlob}
                  showHeader={true}
                  inModal={true}
                  headerClassName={styles.pdfHeader}
                  title={""}
                  onCloseButton={() => setShowPreview(false)}
                  withoutBorder={true}
                  onlyPDFButtons={false}
                />
              )}
              {!isLoading2 && showInstallPlugin && (
                <PDFViewer
                  className={styles.viewer}
                  file={new Blob([])}
                  showHeader={true}
                  headerClassName={styles.title}
                  showVersion={true}
                  showTitle={true}
                  title={"Podgląd"}
                  onCloseButton={() => setShowPreview(false)}
                  withoutBorder={true}
                />
              )} */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatMessageAttachToDossierModal;
